import {useEffect, useRef, useState} from 'react';
import {
    Layout,
    notification, FloatButton, Drawer
} from 'antd';
import {translateCardSignalText} from "../../../../shared/http/textTranslator";
import {FullBook, WordList} from "../../../../shared/types/api";
import {useNavigate, useParams} from "react-router-dom";
import Constants from "../../../../const/Constants";
import {useAppDispatch, useAppSelector} from "../../../../store/hooks";
import {wordListsDataSelector, wordsListsLoadingStatusSelector} from "../../../../store/wordLists";
import {fetchWorldLists} from "../../../../store/wordLists/extraReducers";
import {createNewWord} from "../../../../store/words/extraReducers";
import WordUtils from "../../../utils/WordUtils";
import {openPremiumPage} from "../../../../common/utils/promocode";
import * as API_SOUND from "../../../../shared/http/sound";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {BookContentView, BookReaderRef} from "./BookContentView";
import {BookTabs} from "./ui/BookTabs";
import * as API_BOOKS from "../../../../shared/http/books";

const {Content, Sider} = Layout;

export const ReadBook = () => {
    const [selectedText, setSelectedText] = useState('');
    const [translatedText, setTranslatedText] = useState('');
    const [translating, setTranslating] = useState(false);
    const [loadingWord, setLoadingWord] = useState(false);
    const [fontSize, setFontSize] = useState(16);
    const [selectedList, setSelectedList] = useState(undefined);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [readChars, setReadChars] = useState(0);

    const [sideOneLang, setSideOneLang] = useState<string>(Constants.LANGUAGES[1].id)
    const [sideTwoLang, setSideTwoLang] = useState<string>(Constants.LANGUAGES[0].id)

    const controllerRef = useRef<AbortController | null>(null);

    const bookReaderRef = useRef<BookReaderRef>(null);

    const wordListsIsLoading = useAppSelector(wordsListsLoadingStatusSelector)
    const dispatch = useAppDispatch()

    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [api, contextHolder] = notification.useNotification();

    const wordLists = useAppSelector(wordListsDataSelector)

    const containerRef = useRef<HTMLDivElement>(null);

    const treeCardLists = wordLists.map((wl: WordList) => {
        return {
            id: wl.id,
            pId: wl.communityId ? wl.communityId + 'COM' : 0,
            value: wl.id,
            title: wl.listName,
            isLeaf: true
        }
    })

    const getUserWordLists = () => dispatch(fetchWorldLists())

    useEffect(() => {
        getUserWordLists()
    }, []);

    const changeWordList = (wordListId: any) => {
        setSelectedList(wordListId)
        if (book?.id) {
            API_BOOKS.bindBook(book?.id, wordListId);
        }

    }

    const updateSideOneLang = (s: string) => {
        setSideOneLang(s)
        if (book?.id) {
            API_BOOKS.bindBook(book?.id, undefined, s);
        }
    }

    const updateSideTwoLang = (s: string) => {
        setSideTwoLang(s)
        if (book?.id) {
            API_BOOKS.bindBook(book?.id, undefined, undefined, s);
        }
    }

    const navigate = useNavigate()

    const openPremium = () => {
        openPremiumPage(navigate)
    }

    //@ts-ignore
    const createNewWordRedux = (wordListId, word) =>
        dispatch(
            createNewWord({
                wordListId: wordListId,
                word: word
            })
        )

    const {id} = useParams()

    const [book, setBook] = useState<FullBook>()


    useEffect(() => {
        if (book?.sideOneLang) {
            setSideOneLang(book.sideOneLang)
        }
        if (book?.sideTwoLang) {
            setSideTwoLang(book.sideTwoLang)
        }
        if (book?.wordsListId) {
            // @ts-ignore
            setSelectedList(book?.wordsListId)
        }
    }, [book]);

    const handleSelection = async () => {
        setTimeout(async () => {
            const selection = window.getSelection()?.toString().trim();
            if (!selection) return;

            // если пользователь выбрал другое слово — отменяем старый запрос
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            setSelectedText(selection);
            setTranslatedText('');
            setTranslating(true);

            // создаём новый контроллер для нового запроса
            const controller = new AbortController();
            controllerRef.current = controller;

            try {
                let translated;

                if (sideTwoLang === sideOneLang) {
                    translated = selection;
                } else {
                    const resp = await translateCardSignalText(selection, sideOneLang, sideTwoLang, controller.signal);
                    translated = resp?.data?.text || '[нет перевода]';
                }

                setTranslatedText(translated);
            } catch (error) {
                // @ts-ignore
                if (error.name === 'AbortError') {
                    console.log('Запрос отменён — будет отправлен новый');
                    return;
                }
                setTranslatedText('[ошибка перевода]');
            } finally {
                if (!controller.signal.aborted) {
                    setTranslating(false);
                }
            }
        }, 80);
    };


    const addToDictionary = async () => {
        if (selectedList) {
            try {
                setLoadingWord(true)
                const word = WordUtils.createNewWordCleanByRegExp(selectedList, selectedText, translatedText);
                const resp = await createNewWordRedux(selectedList, word)
                //@ts-ignore
                if (!resp.error) {
                    api.success({
                        message: `Успешно`,
                        description: 'Карточка добавлена',
                        placement: 'topRight',
                    });
                }
            } finally {
                setLoadingWord(false)
            }

        } else {
            api.warning({
                message: `Ошибка`,
                description: 'Выберите список карточек',
                placement: 'topRight',
            });
        }
    }
    const disableSelected = selectedText.length >= Constants.MAX_CARD_SIDE_LENGTH;
    const disableTranslated = translatedText.length >= Constants.MAX_CARD_SIDE_LENGTH;
    const disableAdd = disableSelected || disableTranslated


    const openBookMark = (offset: number) => {
        bookReaderRef?.current?.openPageByOffset(offset)
    }

    const playWord = (): void => {
        //@ts-ignore
        API_SOUND.playText(
            Constants.LANGUAGES[1].id,
            selectedText)
    };

    return (
        <Layout style={{height: '100vh'}}>
            {contextHolder}
            <Content style={{padding: '24px', overflow: 'auto'}} onPointerUp={handleSelection}>
                <BookContentView
                    setBook={setBook}
                    ref={bookReaderRef}
                    fontSize={fontSize}
                    book={book}
                    readChars={readChars}
                    setReadChars={setReadChars}/>
            </Content>
            {!isMobile && (
                <Sider width={360} style={{background: 'var(--color-bg-surface)', padding: '24px', borderLeft: '1px solid var(--color-bg-muted)'}}>
                    <BookTabs
                        selectedText={selectedText}
                        disableSelected={disableSelected}
                        playWord={playWord}
                        translating={translating}
                        disableTranslated={disableTranslated}
                        translatedText={translatedText}
                        disableAdd={disableAdd}
                        loadingWord={loadingWord}
                        addToDictionary={addToDictionary}
                        selectedList={selectedList}
                        changeWordList={changeWordList}
                        treeCardLists={treeCardLists}
                        wordListsIsLoading={wordListsIsLoading}
                        book={book}
                        sideOneLang={sideOneLang}
                        sideTwoLang={sideTwoLang}
                        setSideOneLang={updateSideOneLang}
                        setSideTwoLang={updateSideTwoLang}
                        readChars={readChars}
                        openBookMark={openBookMark}
                    />
                </Sider>
            )}
            {isMobile && (
                <>
                    <FloatButton
                        shape="circle"
                        tooltip="Перевод/Содержание"
                        onClick={() => setToolsOpen(true)}
                        style={{right: 16, bottom: 72}}
                    />
                    <Drawer
                        placement="right"
                        open={toolsOpen}
                        onClose={() => setToolsOpen(false)}
                        title="Инструменты"
                        width="100%"             // на маленьких — на весь экран
                        bodyStyle={{padding: 16}}
                    >
                        <BookTabs
                            selectedText={selectedText}
                            disableSelected={disableSelected}
                            playWord={playWord}
                            translating={translating}
                            disableTranslated={disableTranslated}
                            translatedText={translatedText}
                            disableAdd={disableAdd}
                            loadingWord={loadingWord}
                            addToDictionary={addToDictionary}
                            selectedList={selectedList}
                            changeWordList={changeWordList}
                            treeCardLists={treeCardLists}
                            wordListsIsLoading={wordListsIsLoading}
                            book={book}
                            sideOneLang={sideOneLang}
                            sideTwoLang={sideTwoLang}
                            setSideOneLang={updateSideOneLang}
                            setSideTwoLang={updateSideTwoLang}
                            readChars={readChars}
                            openBookMark={openBookMark}
                        />
                    </Drawer>
                </>
            )}
        </Layout>
    );
};
